import { useEffect, useState } from 'react'
import { Building2, MapPin, User, Briefcase, ToggleLeft, IdCard, ArrowDownAZ, Users, UserSquare, FileText, FileSpreadsheet } from 'lucide-react'
import api from '../services/api'
import LookupSelect from '../components/LookupSelect'
import { useEmpresa } from '../contexts/EmpresaContext'
import { Linha } from './LancamentoDepositos'
import { SeletorMultiplo } from './RelatorioConsumoArla'

// Relatório de Funcionários (WRELAFUN do ERP) — por ora só a opção "Geral" (RMOTCOD / RMOTALF)

interface Linha { motcod: string; nome: string; cargo: string; pessoa: string; documento: string; estab: number; filial: string; situacao: string }

export default function RelatorioFuncionarios() {
  const { empresas, empresaCod } = useEmpresa()
  const [empresasSel, setEmpresasSel] = useState<string[]>([]) // vazio = todas
  const [filial, setFilial] = useState('')
  const [funcCod, setFuncCod] = useState('')
  const [funcNome, setFuncNome] = useState('')
  const [pessoa, setPessoa] = useState('')
  const [cargo, setCargo] = useState('')
  const [situacao, setSituacao] = useState('')
  const [classe, setClasse] = useState('')
  const [ordem, setOrdem] = useState<'C' | 'N'>('C')
  const [cargos, setCargos] = useState<{ catcod: string; catdes: string }[]>([])
  const [res, setRes] = useState<Linha[] | null>(null)
  const [carregando, setCarregando] = useState(false)
  const [gerando, setGerando] = useState<'' | 'pdf' | 'excel'>('')
  const [erro, setErro] = useState('')

  useEffect(() => { api.get('/funcionarios/cargos').then(r => setCargos(r.data)).catch(() => {}) }, [])

  const params = () => ({ empresas: empresasSel.join(','), filial, motcod: funcCod, pessoa, cargo, situacao, classe, ordem, empresa: empresaCod ?? 1 })

  async function gerar() {
    setErro('')
    setCarregando(true)
    try {
      const r = await api.get('/funcionarios/relatorio/geral', { params: params() })
      setRes(r.data)
    } catch (e: any) {
      setRes(null)
      setErro(e?.response?.data?.error || 'Erro ao gerar relatório.')
    } finally {
      setCarregando(false)
    }
  }

  async function exportar(formato: 'pdf' | 'excel') {
    setErro('')
    setGerando(formato)
    try {
      const r = await api.get('/funcionarios/relatorio/geral', { params: { ...params(), formato }, responseType: 'blob' })
      const tipoArq = formato === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      const url = URL.createObjectURL(new Blob([r.data], { type: tipoArq }))
      if (formato === 'pdf') {
        window.open(url, '_blank')
      } else {
        const a = document.createElement('a')
        a.href = url
        a.download = 'relatorio-funcionarios.xlsx'
        a.click()
        URL.revokeObjectURL(url)
      }
    } catch {
      setErro(formato === 'pdf' ? 'Erro ao gerar PDF.' : 'Erro ao gerar Excel.')
    } finally {
      setGerando('')
    }
  }

  const th: React.CSSProperties = { padding: '0.5rem 0.6rem', fontSize: '0.68rem', fontWeight: 800, color: '#fff', background: '#1b3f7b', textAlign: 'left', whiteSpace: 'nowrap', textTransform: 'uppercase' }
  const td: React.CSSProperties = { padding: '0.4rem 0.6rem', fontSize: '0.82rem', borderBottom: '1px solid #eef2f7', textAlign: 'left', whiteSpace: 'nowrap' }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', width: '100%' }}>
      <div className="modal-box" style={{ width: '100%', maxWidth: 680, maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
        <div className="modal-header" style={{ background: 'linear-gradient(135deg, #1a6ea8, #14547f)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem' }}>
            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 34, height: 34, borderRadius: 9, background: 'rgba(255,255,255,0.15)' }}>
              <Users size={18} color="#fff" />
            </span>
            <div>
              <h2>Relatório de Funcionários</h2>
              <div style={{ fontSize: '0.7rem', color: '#dbeafe', marginTop: 2 }}>Relação geral de funcionários com função, documento e situação</div>
            </div>
          </div>
        </div>

        <div className="modal-corpo">
          <Linha label="Estabelecimento" icon={Building2}>
            <SeletorMultiplo opcoes={empresas.map(e => ({ cod: String(e.empcod), des: `${e.empcod} — ${e.empfan?.trim() || e.empnom?.trim()}` }))}
              selecionados={empresasSel} onChange={setEmpresasSel} placeholderBusca="Buscar empresa..." />
          </Linha>

          <Linha label="Filial" icon={MapPin}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <input value={filial} onChange={e => setFilial(e.target.value)} maxLength={2} style={{ maxWidth: 70 }} />
              <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>em branco = todas</span>
            </div>
          </Linha>

          <Linha label="Funcionário" icon={User}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <div style={{ minWidth: 300 }}>
                <LookupSelect endpoint="/funcionarios" extraParams={{ status: 'todos' }} codKey="motcod" descKey="motnome" tagKey="motcod"
                  codValue={funcCod} descValue={funcNome}
                  onSelect={(cod, desc) => { setFuncCod(cod); setFuncNome(desc) }}
                  placeholder="Todos" listWidth={320} />
              </div>
              {funcCod
                ? <button className="btn btn-secondary" style={{ padding: '0.25rem 0.6rem', fontSize: '0.72rem' }} onClick={() => { setFuncCod(''); setFuncNome('') }}>Todos</button>
                : <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>em branco = todos</span>}
            </div>
          </Linha>

          <Linha label="Pessoa" icon={UserSquare}>
            <select value={pessoa} onChange={e => setPessoa(e.target.value)} style={{ maxWidth: 160 }}>
              <option value="">Todos</option>
              <option value="F">Física</option>
              <option value="J">Jurídica</option>
            </select>
          </Linha>

          <Linha label="Função" icon={Briefcase}>
            <select value={cargo} onChange={e => setCargo(e.target.value)} style={{ maxWidth: 320 }}>
              <option value="">Todas</option>
              {cargos.map(c => <option key={c.catcod} value={c.catcod}>{c.catdes}</option>)}
            </select>
          </Linha>

          <Linha label="Situação" icon={ToggleLeft}>
            <select value={situacao} onChange={e => setSituacao(e.target.value)} style={{ maxWidth: 160 }}>
              <option value="">Todas</option>
              <option value="A">Ativos</option>
              <option value="I">Inativos</option>
            </select>
          </Linha>

          <Linha label="Classe CNH" icon={IdCard}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
              <input value={classe} onChange={e => setClasse(e.target.value.toUpperCase())} maxLength={3} style={{ maxWidth: 80 }} />
              <span style={{ fontSize: '0.75rem', color: '#94a3b8' }}>em branco = todas</span>
            </div>
          </Linha>

          <Linha label="Classificação" icon={ArrowDownAZ}>
            <select value={ordem} onChange={e => setOrdem(e.target.value as 'C' | 'N')} style={{ maxWidth: 160 }}>
              <option value="C">Código</option>
              <option value="N">Nome</option>
            </select>
          </Linha>

          {erro && (
            <div style={{ marginTop: '0.9rem', fontSize: '0.8rem', fontWeight: 600, padding: '0.55rem 0.9rem', borderRadius: 8, background: '#fee2e2', color: '#991b1b', border: '1px solid #fecaca' }}>
              {erro}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-primary" onClick={gerar} disabled={carregando}>{carregando ? 'Gerando...' : 'Gerar'}</button>
          <button className="btn" style={{ background: '#dc2626', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
            onClick={() => exportar('pdf')} disabled={!!gerando}>
            <FileText size={15} /> {gerando === 'pdf' ? 'Gerando PDF...' : 'PDF'}
          </button>
          <button className="btn" style={{ background: '#15803d', color: '#fff', display: 'inline-flex', alignItems: 'center', gap: '0.35rem' }}
            onClick={() => exportar('excel')} disabled={!!gerando}>
            <FileSpreadsheet size={15} /> {gerando === 'excel' ? 'Gerando Excel...' : 'Excel'}
          </button>
        </div>
      </div>

      {res && (
        <div className="modal-box" style={{ width: '100%', maxWidth: 'none', maxHeight: 'none', boxShadow: '0 12px 32px -8px rgba(15,23,42,0.18)' }}>
          <div className="modal-corpo" style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
            {res.length === 0 && <div style={{ textAlign: 'center', color: '#94a3b8', padding: '1.2rem' }}>Nenhum funcionário encontrado.</div>}

            {res.length > 0 && (
              <>
                <div style={{ overflowX: 'auto', border: '1px solid #bfdbfe', borderRadius: 8 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr>
                        <th style={th}>Código</th><th style={th}>Nome</th><th style={th}>Função</th><th style={th}>Pessoa</th>
                        <th style={th}>CPF/CNPJ</th><th style={{ ...th, textAlign: 'right' }}>Estab.</th><th style={th}>Filial</th><th style={th}>Situação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {res.map((l, i) => (
                        <tr key={l.motcod} style={{ background: i % 2 ? '#f5f9fd' : '#fff' }}>
                          <td style={{ ...td, fontWeight: 600 }}>{l.motcod}</td>
                          <td style={td}>{l.nome}</td>
                          <td style={td}>{l.cargo}</td>
                          <td style={td}>{l.pessoa}</td>
                          <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{l.documento}</td>
                          <td style={{ ...td, textAlign: 'right' }}>{l.estab || ''}</td>
                          <td style={td}>{l.filial}</td>
                          <td style={{ ...td, color: l.situacao === 'Ativo' ? '#047857' : '#b91c1c', fontWeight: 600 }}>{l.situacao}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div style={{ borderRadius: 12, padding: '0.8rem 1.1rem', background: 'linear-gradient(135deg, #0f2a52, #1b3f7b)', color: '#fff', fontWeight: 800, letterSpacing: '0.02em' }}>
                  TOTAL DE REGISTROS: {res.length}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
